import {
  GqlModel,
  GqlModelListOperator,
  GqlModelOperator,
  GqlMutationModel,
  GqlQueryModel,
} from "../graphql/graphql.model";
import gql from "graphql-tag";
import {JSONModel, jsonProperty} from "../json.model";
import {ProgressBarModel} from "../misc/percentage-bar.model";
import {QuickFormArray, QuickFormGroup} from "../helpers/quick-form-group";
import {DefaultNameIndexEditingGqlModel} from "../graphql/common.gql-model";
import {FormList} from "../helpers/form-list";

export const deploymentFormKeys = [
  "name",
  "sourceName",
  "workOrderEndSignature",
  "estimatedCompleteDate",
  "estimatedInstallationDate",
  "requestedInstallationDate",
  "estimatedSubmissionDate",
  "qualityChecklist",
  "partnerPurchaseOrders",
  "installationInstructionsMAI",
  "installationInstructionsLease",
];

export const deploymentFragment: any = gql`
  fragment deploymentData on Deployment {
    id
    name
    plannedAssets
    deployedAssets
    plannedAccessories
    deployedAccessories
    deployedStandAloneAccessories
    plannedItems
    deployedItems
    deliveryDate
    installDate
    installed
    siteName
    projectName
    siteId
    projectId
    isMigrationActive
    workflowId
    deployedToK2PercentageBar {
      visible
      title
      value
      type
      width
    }
    pastSchedule
    installationInstructionsMAI
    installationInstructionsLease
    estimatedCompleteDate
    estimatedInstallationDate
    estimatedSubmissionDate
    requestedInstallationDate
    readyForInstallation
    qualityChecklist
    workOrderEndSignature
    sourceName
    partnerPurchaseOrders {
      name
      selected
    }
    rejected
    rejectedReason
    rejectedDate
  }
`;

export class DeploymentPartnerPurchaseOrderItemModel extends JSONModel {
  @jsonProperty("string") name: string;
  @jsonProperty("boolean") selected: boolean;
}

export class DeploymentPartnerPurchaseOrderModel extends FormList<DeploymentPartnerPurchaseOrderItemModel> {
  constructor(items?: any[]) {
    super({formKeys: ["selected"], items: items || []});
  }
}

export class DeploymentGqlModel extends GqlQueryModel {
  static mapper: GqlModelOperator<DeploymentGqlModel> = GqlModel.buildMapper(DeploymentGqlModel, "deployment");

  static query: any = gql`
    ${deploymentFragment}

    query getDeployment($id: ID) {
      deployment: deployment(id: $id) {
        ...deploymentData
      }
    }
  `;

  @jsonProperty("string") id: string;
  @jsonProperty("string") name: string;
  @jsonProperty("number0") plannedAssets: number;
  @jsonProperty("number0") deployedAssets: number;
  @jsonProperty("number0") plannedAccessories: number;
  @jsonProperty("number0") deployedAccessories: number;
  @jsonProperty("number0") deployedStandAloneAccessories: number;
  @jsonProperty("number0") plannedItems: number;
  @jsonProperty("number0") deployedItems: number;
  @jsonProperty("date") deliveryDate: Date;
  @jsonProperty("date") installDate: Date;
  @jsonProperty("boolean") installed: boolean;
  @jsonProperty("string") siteName: string;
  @jsonProperty("string") projectName: string;
  @jsonProperty("string") siteId: string;
  @jsonProperty("string") projectId: string;
  @jsonProperty("boolean") isMigrationActive: boolean;
  @jsonProperty("string") workflowId: string;
  @jsonProperty(ProgressBarModel) deployedToK2PercentageBar: ProgressBarModel;
  @jsonProperty("boolean") pastSchedule: boolean;
  @jsonProperty("string", "pastSchedule") pastScheduleMessage: string;
  @jsonProperty("stringDate") estimatedCompleteDate: string;
  @jsonProperty("stringDate") estimatedInstallationDate: string;
  @jsonProperty("stringDate") estimatedSubmissionDate: string;
  @jsonProperty("stringDate") requestedInstallationDate: string;
  @jsonProperty("boolean") readyForInstallation: boolean;
  @jsonProperty("boolean") workOrderEndSignature: boolean;
  @jsonProperty("string") sourceName: string;
  @jsonProperty("boolean") rejected: boolean;
  @jsonProperty("string") rejectedReason: string;
  @jsonProperty("string") rejectedDate: string;

  @jsonProperty(DeploymentPartnerPurchaseOrderModel) partnerPurchaseOrders: DeploymentPartnerPurchaseOrderModel;

  @jsonProperty(DefaultNameIndexEditingGqlModel) qualityChecklist: DefaultNameIndexEditingGqlModel;

  @jsonProperty(DefaultNameIndexEditingGqlModel)
  installationInstructionsMAI: DefaultNameIndexEditingGqlModel;

  @jsonProperty(DefaultNameIndexEditingGqlModel)
  installationInstructionsLease: DefaultNameIndexEditingGqlModel;

  form: QuickFormGroup = new QuickFormGroup(this, deploymentFormKeys);

  static variables(id: string) {
    return {id};
  }
}

export class DeploymentListGqlModel extends GqlQueryModel {
  static mapper: GqlModelOperator<DeploymentListGqlModel> = GqlModel.buildMapper(DeploymentListGqlModel);

  static query: any = gql`
    ${deploymentFragment}

    query getDeployments($projectId: ID, $siteId: ID, $id: ID, $search: String, $limit: Int, $page: Int, $filterOutComplete: Boolean) {
      rows: deployments(projectId: $projectId, siteId: $siteId, id: $id, search: $search, limit: $limit, page: $page, filterOutComplete: $filterOutComplete) {
        ...deploymentData
      }

      count: deploymentCount(projectId: $projectId, siteId: $siteId, id: $id, search: $search, filterOutComplete: $filterOutComplete)
    }
  `;

  @jsonProperty([DeploymentGqlModel]) rows: DeploymentGqlModel[];
  @jsonProperty("number0") count: number;

  form: QuickFormArray = new QuickFormArray(this.rows, deploymentFormKeys);

  static variables({projectId, siteId, id, search, limit, page, filterOutComplete}) {
    return {
      ...projectId && {projectId},
      ...siteId && {siteId},
      ...id && {id},
      ...search && {search},
      limit: +limit,
      page: +page,
      filterOutComplete: !!filterOutComplete,
      doCount: !id,
    };
  }
}

export interface DeploymentInputGqlModel {
  id: string;
}

export class DeploymentUpdateGqlModel extends GqlModel implements DeploymentInputGqlModel {
  static mapIndexedList(obj: DefaultNameIndexEditingGqlModel) {
    if (!obj || !obj.items) return [];
    return obj.items.map(it => it.name);
  }

  static mapPurchaseOrders(obj: DeploymentPartnerPurchaseOrderModel) {
    if (!obj || !obj.items) return [];
    return obj.items.filter(it => it.selected).map(it => it.name);
  }

  @jsonProperty("string") id: string;
  @jsonProperty("string") siteId: string;
  @jsonProperty("string") projectId: string;
  @jsonProperty("string") name: string;
  @jsonProperty("stringDate") estimatedCompleteDate: string;
  @jsonProperty("stringDate") estimatedInstallationDate: string;
  @jsonProperty("stringDate") estimatedSubmissionDate: string;
  @jsonProperty("stringDate") requestedInstallationDate: string;
  @jsonProperty("boolean") workOrderEndSignature: boolean;
  @jsonProperty("string") sourceName: string;
  @jsonProperty(DeploymentUpdateGqlModel.mapPurchaseOrders) partnerPurchaseOrders: string[];
  @jsonProperty(DeploymentUpdateGqlModel.mapIndexedList) qualityChecklist: string[];
  @jsonProperty(DeploymentUpdateGqlModel.mapIndexedList) installationInstructionsMAI: string[];
  @jsonProperty(DeploymentUpdateGqlModel.mapIndexedList) installationInstructionsLease: string[];
}

export class DeploymentRemoveGqlModel extends GqlModel implements DeploymentInputGqlModel {
  @jsonProperty("string") id: string;
  @jsonProperty("booleanTrue") remove: boolean;

  constructor(id: string) {
    super({id, remove: true});
  }
}

export class DeploymentRejectGqlModel extends GqlModel implements DeploymentInputGqlModel {
  @jsonProperty("string") id: string;
  @jsonProperty("string") reject: string;

  constructor(id: string, reason: string) {
    super({id, reject: reason});
  }
}

export class DeploymentUnRejectGqlModel extends GqlModel implements DeploymentInputGqlModel {
  @jsonProperty("string") id: string;
  @jsonProperty("booleanTrue") unReject: boolean;

  constructor(id: string) {
    super({id, unReject: true});
  }
}

export class DeploymentMutateGqlModel extends GqlMutationModel {
  static mapper: GqlModelOperator<DeploymentGqlModel> = GqlModel.buildMapper(DeploymentGqlModel, "deployment");

  @jsonProperty() deployment: DeploymentInputGqlModel;

  static query = gql`
    ${deploymentFragment}

    mutation updateDeployment($deployment: DeploymentInput!) {
      deployment: deployment(deployment: $deployment) {
        ...deploymentData
      }
    }
  `;

  constructor(deployment: DeploymentInputGqlModel) {
    super({deployment});
  }
}

export class SiteForDeploymentGqlModel extends GqlQueryModel {
  static mapper: GqlModelListOperator<SiteForDeploymentGqlModel> =
    GqlModel.buildListMapper(SiteForDeploymentGqlModel, "sites");

  @jsonProperty("string") siteId;
  @jsonProperty("string") siteName;

  static query = gql`
    query getSitesForDeployment($projectId: ID!) {
      sites: sitesForDeployment(projectId: $projectId) {
        siteId
        siteName
      }
    }
  `;

  static variables(projectId: string) {
    return {projectId};
  }
}
